import { useState, useEffect, useRef } from 'react';

interface QueryParams {
  page: number;
  pageSize: number;
  vendorId?: number;
  businessUnitId?: number | string;
  search?: string;
  sortField?: string;
  sortOrder?: string;
}

interface TableData {
  data: any[];
  page: number;
  totalCount: number;
}

const useTable = (
  token: string | null,
  getDataRequest: Function,
  selectedVendorId?: number,
  selectedBusinessUnitId?: number,
  searchTerm: string = '',
  reload?: boolean,
  setReload?: Function,
  businessUnitsIds?: string,
) => {
  const [tableData, setTableData] = useState<TableData>({
    data: [],
    page: 0,
    totalCount: 0,
  });

  const [tableKey, setTableKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const isMounted = useRef(true);

  // Initialize component mounted state
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Build query parameters
  const buildQueryParams = (): QueryParams => {
    const params: QueryParams = {
      page: currentPage + 1,
      pageSize: rowsPerPage,
    };

    if (selectedVendorId) {
      params.vendorId = selectedVendorId;
    }

    // Use selectedBusinessUnitId if provided, otherwise use businessUnitsIds for "All" case
    if (selectedBusinessUnitId) {
      params.businessUnitId = selectedBusinessUnitId;
    } else if (businessUnitsIds) {
      params.businessUnitId = businessUnitsIds;
    }

    if (searchTerm && searchTerm.trim() !== '') {
      params.search = searchTerm;
    }

    if (sortField) {
      params.sortField = sortField;
      params.sortOrder = sortOrder;
    }

    return params;
  };

  // Fetch data function
  const fetchData = async (params: QueryParams): Promise<TableData> => {
    try {
      setLoading(true);

      const queryString = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');

      const result = await getDataRequest(queryString, token);

      const newTableData: TableData = {
        data: result.data || [],
        page: result.meta.currentPage - 1,
        totalCount: result.meta.totalRecords || 0,
      };

      return newTableData;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load data effect
  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      try {
        const params = buildQueryParams();
        const newData = await fetchData(params);

        if (isMounted.current) {
          setTableData(newData);
          setHasInitialLoad(true);
          setReload?.(false);
          setTableKey(prev => prev + 1);
        }
      } catch (error) {
        if (isMounted.current) {
          setTableData({ data: [], page: 0, totalCount: 0 });
          setHasInitialLoad(true);
        }
      }
    };

    loadData();
  }, [selectedVendorId, selectedBusinessUnitId, businessUnitsIds, searchTerm, reload, currentPage, rowsPerPage, sortField, sortOrder, token]);

  // Handle sorting
  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(direction);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(tableData.totalCount / rowsPerPage);
    const validPage = Math.min(Math.max(0, newPage - 1), totalPages - 1);
    setCurrentPage(validPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedVendorId, selectedBusinessUnitId, searchTerm]);

  return {
    tableData,
    tableKey,
    loading,
    rowsPerPage,
    currentPage,
    hasInitialLoad,
    sortField,
    sortOrder,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange
  };
};

export default useTable;
